/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { AuthFacade } from '~backoffice/app/~store/facades/auth.facade';
import { Pagination, RequestParams } from '~core/models/core.models';
import {
  Organization,
  OrganizationCreationParams,
  OrganizationFiltersValues,
  OrganizationListParams,
  OrganizationUser,
  OrganizationUserCreationParams,
  OrganizationUserScopeCreationParams,
  UploadOrganizationLogoParams
} from '~core/models/organization.models';
import { ApiConstantsService } from '~core/services/api-constants.service';
import {
  CreateCustomerSupplierParams,
  CustomerSupplier
} from '~core/models/customer-supplier.model';

@Injectable()
export class OrganizationService {
  private _http = inject(HttpClient);
  private _apiConstantsService = inject(ApiConstantsService);
  protected _authFacade = inject(AuthFacade);

  list(params?: OrganizationListParams): Observable<Pagination<Organization>> {
    return this._http.get<Pagination<Organization>>(
      this._apiConstantsService.organizationUrl,
      {
        params: {
          ...params,
          sort: params?.sort || 'createdAt',
          desc: params?.desc || 'createdAt'
        } as Record<string, any>
      }
    );
  }

  read(
    organizationId: string,
    params?: OrganizationListParams
  ): Observable<Organization> {
    return this._http.get<Organization>(
      `${this._apiConstantsService.organizationUrl}/${organizationId}`,
      { params }
    );
  }

  create(payload: OrganizationCreationParams): Observable<Organization> {
    return this._http.post<Organization>(
      this._apiConstantsService.organizationUrl,
      payload
    );
  }

  update(
    organizationId: string,
    payload: Partial<OrganizationCreationParams>
  ): Observable<Organization> {
    return this._http.patch<Organization>(
      `${this._apiConstantsService.organizationUrl}/${organizationId}`,
      payload
    );
  }

  toggleState(
    organizationId: string,
    enabled: boolean
  ): Observable<Organization> {
    return this._http.patch<Organization>(
      `${this._apiConstantsService.organizationUrl}/${organizationId}`,
      { enabled }
    );
  }

  toggleApiTokenState(
    organizationId: string,
    enabledApiToken: boolean
  ): Observable<Organization> {
    return this._http.patch<Organization>(
      `${this._apiConstantsService.organizationUrl}/${organizationId}/enabled-api-token`,
      { enabledApiToken }
    );
  }

  delete(organizationId: string): Observable<unknown> {
    return this._http.delete(
      `${this._apiConstantsService.organizationUrl}/${organizationId}`
    );
  }

  private get _organizationId(): string {
    return this._authFacade.currentUser!.organization.id || '';
  }

  private get _organizationUserUrl(): string {
    return `${this._apiConstantsService.organizationUrl}/${this._organizationId}/users`;
  }

  // Organization user scopes
  listUser(
    userId: string,
    params?: RequestParams & {
      [key: string]: any;
    }
  ): Observable<Pagination<OrganizationUser>> {
    return this._http.get<Pagination<OrganizationUser>>(
      `${this._organizationUserUrl}/${userId}/scopes`,
      { params }
    );
  }

  updateUser(
    userId: string,
    payload: Partial<OrganizationUserCreationParams[]>
  ): Observable<OrganizationUser> {
    return this._http.put<OrganizationUser>(
      `${this._organizationUserUrl}/${userId}/scopes`,
      { scopes: payload }
    );
  }

  //  Set a user's scope role by establishment
  updateUserScopes(
    organizationId: string,
    userId: string,
    payload: OrganizationUserScopeCreationParams
  ): Observable<OrganizationUser> {
    return this._http.put<OrganizationUser>(
      `${this._apiConstantsService.organizationUrl}/${organizationId}/users/${userId}/scopes/set`,
      payload
    );
  }

  deleteUser(
    userId: string,
    payload: { scopes: { establishmentId: string; roleId: string }[] }
  ): Observable<unknown> {
    return this._http.delete(`${this._organizationUserUrl}/${userId}/scopes`, {
      body: payload
    });
  }

  uploadLogo(params: UploadOrganizationLogoParams): Observable<Organization> {
    const formData = new FormData();
    formData.append('organizationId', params.organizationId);
    formData.append('logo', params.file);

    return this._http.post<Organization>(
      `${this._apiConstantsService.organizationUrl}/${params.organizationId}`,
      formData
    );
  }

  listPartners(
    organizationId: string,
    params?: RequestParams & {
      [key: string]: any;
    }
  ): Observable<Pagination<CustomerSupplier>> {
    return this._http.get<Pagination<CustomerSupplier>>(
      `${this._apiConstantsService.organizationUrl}/${organizationId}/partners`,
      { params }
    );
  }

  createPartner(
    organizationId: string,
    data: CreateCustomerSupplierParams
  ): Observable<CustomerSupplier> {
    return this._http.post<CustomerSupplier>(
      `${this._apiConstantsService.organizationUrl}/${organizationId}/partners`,
      {
        ...data,
        useStateFormViolationHandler: true
      }
    );
  }

  publicSearch(organizationReference: string): Observable<Organization> {
    return this._http.get<Organization>(
      `${this._apiConstantsService.identityMicroservice}/public-search/organizations/${organizationReference}`
    );
  }

  getOrganizationFiltersValues(): Observable<OrganizationFiltersValues> {
    return this._http.get<Record<keyof OrganizationFiltersValues, string[]>>(
      this._apiConstantsService.organizationFiltersValuesUrl
    );
  }
}
